import { ChevronDown } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Spinner } from '@/components/ui';
import { Button, ModalContentWrapper, SelectButtonsNew } from '@/components/ui';
import {
  useDocumentChecklist,
  useDocumentMatrix,
  useLoanDocuments,
  useSetLegalRef,
} from '@/hooks/use-document-manager';
import { cn } from '@/lib/cn';
import type { DocumentCategory } from '@/types';
import { DOCUMENT_CATEGORY_LABELS } from '@/types';

const CATEGORIES: DocumentCategory[] = [
  'LOAN_AGREEMENT',
  'SECURITY_DOCUMENTS',
  'VALUATION',
  'INSURANCE',
  'BORROWER_ID',
  'FINANCIAL_STATEMENTS',
  'LEGAL_ADVICE',
  'CORRESPONDENCE',
  'SETTLEMENT',
  'DISCHARGE',
  'OTHER',
];

interface LegalRefModalProps {
  open: boolean;
  onClose: () => void;
  currentRef?: string;
  loanId: string;
  onSuccess: () => void;
}

function LegalRefModal({ open, onClose, currentRef, loanId, onSuccess }: LegalRefModalProps) {
  const [ref, setRef] = useState(currentRef || '');
  const setLegalRef = useSetLegalRef(loanId);

  useEffect(() => {
    if (open) {
      setRef(currentRef || '');
    }
  }, [currentRef, open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await setLegalRef.mutateAsync(ref);
    onSuccess();
    onClose();
  };

  if (!open) return null;

  return (
    <ModalContentWrapper open onClose={onClose} title="Legal Matter Reference">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Reference Number</label>
          <input
            type="text"
            value={ref}
            onChange={e => setRef(e.target.value)}
            placeholder="e.g., LM-2024-12345"
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        <div className="flex gap-2 pt-4">
          <Button variant="secondary" type="button" onClick={onClose} className="w-fit">
            Cancel
          </Button>
          <Button
            variant="primary"
            type="submit"
            disabled={setLegalRef.isPending}
            className="w-fit flex-1 px-4 py-2 text-white rounded-md text-sm font-medium hover: disabled:opacity-50 transition-colors"
          >
            {setLegalRef.isPending ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </form>
    </ModalContentWrapper>
  );
}

export function DocumentManagerTab({
  loanId,
  legalMatterRef,
}: {
  loanId: string;
  legalMatterRef?: string;
}) {
  const [viewMode, setViewMode] = useState<'checklist' | 'list' | 'matrix'>('checklist');
  const [legalRefOpen, setLegalRefOpen] = useState(false);
  const [expandedDoc, setExpandedDoc] = useState<string | null>(null);
  const { data: documents, isLoading: docsLoading } = useLoanDocuments(loanId);
  const { data: checklist, isLoading: checklistLoading } = useDocumentChecklist(loanId);
  const { data: matrix, isLoading: matrixLoading } = useDocumentMatrix(loanId);
  const matrixItemsByCategory = useMemo(
    () => new Map((matrix?.items ?? []).map(item => [item.category, item])),
    [matrix?.items],
  );

  const isLoading = docsLoading || checklistLoading || matrixLoading;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" className="w-5 h-5 animate-spin text-gray-400 mr-2" />
        <span className="text-gray-400">Loading documents...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Legal Matter Reference */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-blue-900">Legal Matter Reference</p>
            <p className="text-sm text-blue-700 mt-1">
              {legalMatterRef?.trim() || 'No reference set'}
            </p>
          </div>
          <button
            onClick={() => setLegalRefOpen(true)}
            className="px-3 py-1.5 text-sm font-medium bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            Edit
          </button>
        </div>
      </div>

      {/* View Mode Selector */}
      <SelectButtonsNew
        value={viewMode}
        onChange={mode => setViewMode(mode as any)}
        options={[
          { value: 'checklist', label: 'Checklist' },
          { value: 'list', label: 'List' },
          { value: 'matrix', label: 'Matrix' },
        ]}
        classes={{
          container: 'w-fit',
          buttonsContainer: 'min-h-0 bg-white border-gray-200',
          button: 'px-3 py-1.5 text-sm font-medium',
        }}
        capitalize={false}
      />

      {/* Checklist View */}
      {viewMode === 'checklist' && checklist && (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
            <h3 className="text-sm font-semibold text-gray-900">Document Checklist</h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 p-6">
            {checklist.map((item, idx) => (
              <div
                key={idx}
                className={cn(
                  'border rounded-lg p-4 transition-colors',
                  item.completed
                    ? 'bg-green-50 border-green-200'
                    : item.required
                      ? 'bg-red-50 border-red-200'
                      : 'bg-gray-50 border-gray-200',
                )}
              >
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    checked={item.completed}
                    readOnly
                    className="mt-1 w-4 h-4 rounded"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900">
                      {DOCUMENT_CATEGORY_LABELS[item.category]}
                    </p>
                    {item.required && !item.completed && (
                      <span className="inline-block text-xs font-medium text-red-700 bg-red-100 px-2 py-1 rounded mt-2">
                        Required
                      </span>
                    )}
                    {item.documentName && (
                      <p className="text-xs text-gray-600 mt-2">{item.documentName}</p>
                    )}
                    {item.lastUpdated && (
                      <p className="text-xs text-gray-500 mt-1">
                        Updated {new Date(item.lastUpdated).toLocaleDateString('en-AU')}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* List View */}
      {viewMode === 'list' && documents && (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
            <h3 className="text-sm font-semibold text-gray-900">Documents</h3>
          </div>
          <div className="divide-y divide-gray-200">
            {documents.length > 0 ? (
              documents.map(doc => (
                <div key={doc.id} className="px-6 py-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium text-gray-900">{doc.name}</h4>
                      {doc.category && (
                        <span className="inline-block text-xs font-medium bg-gray-100 text-gray-800 px-2 py-1 rounded mt-2">
                          {DOCUMENT_CATEGORY_LABELS[doc.category]}
                        </span>
                      )}
                      <p className="text-sm text-gray-600 mt-2">
                        {doc.versionCount} version{doc.versionCount !== 1 ? 's' : ''}
                        {doc.latestVersion &&
                          ` - Latest: ${new Date(doc.latestVersion.createdAt).toLocaleDateString('en-AU')}`}
                      </p>
                    </div>
                    <button
                      onClick={() => setExpandedDoc(expandedDoc === doc.id ? null : doc.id)}
                      className="ml-4 text-gray-400 hover:text-gray-600"
                    >
                      <ChevronDown
                        className={cn(
                          'w-5 h-5 transition-transform',
                          expandedDoc === doc.id ? 'rotate-180' : '',
                        )}
                      />
                    </button>
                  </div>

                  {expandedDoc === doc.id && doc.latestVersion && (
                    <div className="mt-4 pt-4 border-t border-gray-200 space-y-2">
                      <div className="bg-gray-50 rounded p-3">
                        <p className="text-sm font-medium text-gray-900">
                          v{doc.latestVersion.versionNumber} -{' '}
                          {new Date(doc.latestVersion.createdAt).toLocaleDateString('en-AU')}
                        </p>
                        {doc.latestVersion.uploadedBy && (
                          <p className="text-xs text-gray-600 mt-1">
                            Uploaded by {doc.latestVersion.uploadedBy.firstName}{' '}
                            {doc.latestVersion.uploadedBy.lastName}
                          </p>
                        )}
                        {doc.latestVersion.changeNote && (
                          <p className="text-xs text-gray-600 mt-2">
                            {doc.latestVersion.changeNote}
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))
            ) : (
              <div className="px-6 py-12 text-center">
                <p className="text-gray-500 text-sm">No documents uploaded yet</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Matrix View */}
      {viewMode === 'matrix' && matrix && (
        <div className="bg-white rounded-lg border border-gray-200 overflow-x-auto">
          <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
            <h3 className="text-sm font-semibold text-gray-900">Document Matrix</h3>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50">
                <th className="px-6 py-3 text-left font-medium text-gray-700">Category</th>
                <th className="px-6 py-3 text-left font-medium text-gray-700">Status</th>
                <th className="px-6 py-3 text-left font-medium text-gray-700">Latest Version</th>
              </tr>
            </thead>
            <tbody>
              {CATEGORIES.map(category => {
                const item = matrixItemsByCategory.get(category);
                const latest = item?.latestVersion;
                return (
                  <tr key={category} className="border-b border-gray-200 hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <span className="font-medium text-gray-900">
                        {DOCUMENT_CATEGORY_LABELS[category]}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      {item?.completed ? (
                        <span className="inline-block px-2.5 py-1 text-xs font-medium bg-green-100 text-green-800 rounded">
                          Complete
                        </span>
                      ) : item?.required ? (
                        <span className="inline-block px-2.5 py-1 text-xs font-medium bg-red-100 text-red-800 rounded">
                          Required
                        </span>
                      ) : (
                        <span className="inline-block px-2.5 py-1 text-xs font-medium bg-gray-100 text-gray-800 rounded">
                          Not provided
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {latest
                        ? `v${latest.versionNumber} - ${new Date(latest.createdAt).toLocaleDateString('en-AU')}`
                        : '-'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <LegalRefModal
        open={legalRefOpen}
        onClose={() => setLegalRefOpen(false)}
        currentRef={legalMatterRef}
        loanId={loanId}
        onSuccess={() => {}}
      />
    </div>
  );
}
